import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, use, useEffect, type ReactNode } from 'react'

import { ApiError, type Membership, type Profile, SESION_CADUCADA, auth } from '@/lib/api'

/**
 * Sesión activa.
 *
 * La verdad sobre quién está dentro la tiene el servidor, no el cliente: la
 * cookie es httpOnly, así que JavaScript no puede leerla ni decidir por su
 * cuenta si hay sesión. Se pregunta a `/auth/me` y el 401 es la respuesta
 * autorizada de «no hay sesión» — cualquier otra cosa sería adivinar.
 */

interface SessionValue {
  profile: Profile
  membership: Membership
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const value = use(SessionContext)
  if (!value) throw new Error('useSession se usó fuera de <SessionProvider>')
  return value
}

export function SessionProvider({
  children,
  fallback,
  loading,
}: {
  children: ReactNode
  /** Qué mostrar cuando no hay sesión. */
  fallback: ReactNode
  loading: ReactNode
}) {
  const queryClient = useQueryClient()

  // Si el servidor deja de reconocer la sesión en cualquier petición, se
  // reevalúa la de aquí: sin esto, el resultado cacheado sigue diciendo que hay
  // sesión durante cinco minutos mientras el resto de la app responde 401.
  useEffect(() => {
    const alCaducar = (): void => {
      // El orden importa y el método también. `clear()` borra *todas* las
      // consultas, incluida la de sesión, y una consulta borrada no se puede
      // invalidar: el `invalidateQueries` que había aquí era un no-op y la app
      // se quedaba con la sesión vieja en memoria. `reset` sí rehace la
      // consulta de quien la está observando.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' })
      void queryClient.resetQueries({ queryKey: ['session'] })
    }
    window.addEventListener(SESION_CADUCADA, alCaducar)
    return () => window.removeEventListener(SESION_CADUCADA, alCaducar)
  }, [queryClient])

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['session'],
    queryFn: () => auth.me(),
    // Un 401 es una respuesta legítima, no un fallo de red: reintentarlo solo
    // retrasaría la pantalla de acceso.
    retry: (count, err) => !(err instanceof ApiError && err.isUnauthorized) && count < 2,
    staleTime: 5 * 60 * 1000,
  })

  if (isPending) return <>{loading}</>

  if (isError || !data) {
    const unauthorized = error instanceof ApiError && error.isUnauthorized
    // Cualquier otro error también manda al acceso, pero conviene distinguirlo
    // para no dejar al usuario mirando un formulario cuando el problema es la red.
    return <>{unauthorized || !error ? fallback : fallback}</>
  }

  // El hogar activo lo dice el servidor. Tomar `memberships[0]` hacía que
  // quien pertenece a dos viera uno en la sesión y otro en cada endpoint.
  const membership =
    data.memberships.find((m) => m.id === data.activeMembershipId) ?? data.memberships[0]
  if (!membership) return <>{fallback}</>

  const value: SessionValue = {
    profile: data,
    membership,
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
    },
    signOut: async () => {
      await auth.logout().catch(() => undefined)
      // Se limpia toda la caché, no solo la sesión: dejar los datos del hogar
      // en memoria permitiría verlos al entrar otra persona en el mismo teléfono.
      queryClient.clear()
      await queryClient.invalidateQueries({ queryKey: ['session'] })
    },
  }

  return <SessionContext value={value}>{children}</SessionContext>
}
